// src/elements.ts
import { defineCustomElement } from "@aria-ui/core";

// src/overlay-anchor.setup.ts
import { useEffect } from "@aria-ui/core";

// src/contexts.ts
import { createContext } from "@aria-ui/core";
var referenceContext = createContext(
  "overlay/reference",
  null
);

// src/overlay-anchor.setup.ts
function useOverlayAnchor(element) {
  const reference = referenceContext.consume(element);
  useEffect(element, () => {
    reference.set(element);
  });
}

// src/overlay-anchor.types.ts
var overlayAnchorProps = {};
var overlayAnchorEvents = {};

// src/overlay-positioner.setup.ts
import {
  mapValues,
  useAnimationFrame
} from "@aria-ui/core";

// src/positioning.ts
import {
  autoUpdate,
  computePosition,
  flip,
  hide,
  inline,
  offset,
  shift,
  size
} from "@floating-ui/dom";
import { isElement } from "@floating-ui/utils/dom";
import { getWindow } from "@zag-js/dom-query";
function updatePlacement(floating, reference, options) {
  if (!floating || !reference) return;
  const middleware = [
    setupOffset(options),
    setupFlip(options),
    setupShift(options),
    setupSize(options),
    setupInline(options),
    setupHide(options)
  ];
  const { placement, strategy } = options;
  let canceled = false;
  const update = async () => {
    var _a, _b, _c;
    if (!reference || !floating || canceled) {
      return;
    }
    const referenceElement = unwrapElement(reference);
    if (referenceElement && !referenceElement.isConnected) {
      return;
    }
    if (options.hoist && popoverAvailable) {
      floating.style.margin = "unset";
      floating.setAttribute("popover", "manual");
      (_a = floating.showPopover) == null ? void 0 : _a.call(floating);
    }
    const pos = await computePosition(reference, floating, {
      placement,
      middleware,
      strategy
    });
    if (canceled) {
      return;
    }
    if (options.hide) {
      const hidden = (
        // Whether the floating element is fully clipped
        ((_b = pos.middlewareData.hide) == null ? void 0 : _b.escaped) || // Whether the reference element is fully clipped
        ((_c = pos.middlewareData.hide) == null ? void 0 : _c.referenceHidden)
      );
      floating.style.visibility = hidden ? "hidden" : "visible";
    }
    const x = roundByDPR(floating, pos.x);
    const y = roundByDPR(floating, pos.y);
    floating.style.position = pos.strategy;
    if (options.transform) {
      floating.style.left = "0";
      floating.style.top = "0";
      floating.style.transform = `translate3d(${x}px,${y}px,0)`;
      if (getDPR(floating) >= 1.5) {
        floating.style.willChange = "transform";
      }
    } else {
      floating.style.left = `${x}px`;
      floating.style.top = `${y}px`;
      floating.style.removeProperty("transform");
    }
    const [side, align] = getSideAndAlignFromPlacement(pos.placement);
    const attributes = {
      "data-side": side,
      "data-align": align,
      "data-mounted": ""
    };
    for (const [key, value] of Object.entries(attributes)) {
      floating.setAttribute(key, value);
    }
  };
  const autoUpdateOptions = typeof options.autoUpdate === "boolean" ? void 0 : options.autoUpdate;
  const cancelAutoUpdate = options.autoUpdate ? autoUpdate(reference, floating, update, autoUpdateOptions) : void 0;
  if (!options.autoUpdate) {
    void update();
  }
  return () => {
    canceled = true;
    cancelAutoUpdate == null ? void 0 : cancelAutoUpdate();
  };
}
function getOverflowOptions(props) {
  return {
    boundary: props.boundary,
    rootBoundary: props.rootBoundary,
    elementContext: props.elementContext,
    altBoundary: props.altBoundary,
    padding: props.overflowPadding
  };
}
function setupOffset(props) {
  if (props.offset == null) return;
  return offset(props.offset);
}
function setupFlip(props) {
  if (!props.flip) return;
  return flip({
    ...getOverflowOptions(props),
    fallbackPlacements: props.flip === true ? void 0 : props.flip,
    // Disable the cross axis check so that `flip()` can work with `shift()`.
    // See also https://floating-ui.com/docs/flip#combining-with-shift
    crossAxis: false
  });
}
function setupShift(props) {
  if (!props.shift) return;
  return shift({
    ...getOverflowOptions(props),
    mainAxis: props.shift,
    crossAxis: props.overlap
  });
}
function setupSize(props) {
  if (!props.fitViewport && !props.sameWidth && !props.sameHeight) return;
  return size({
    ...getOverflowOptions(props),
    apply({ elements, rects, availableHeight, availableWidth }) {
      const floating = elements.floating;
      if (props.sameWidth) {
        floating.style.width = `${Math.round(rects.reference.width)}px`;
      }
      if (props.sameHeight) {
        floating.style.height = `${Math.round(rects.reference.height)}px`;
      }
      if (props.fitViewport) {
        floating.style.maxWidth = `${Math.floor(availableWidth)}px`;
        floating.style.maxHeight = `${Math.floor(availableHeight)}px`;
      }
    }
  });
}
function setupInline(props) {
  if (!props.inline) return;
  return inline();
}
function setupHide(props) {
  if (!props.hide) return;
  return hide({
    padding: props.overflowPadding,
    elementContext: "reference"
  });
}
function roundByDPR(element, value) {
  const dpr = getDPR(element);
  return Math.round(value * dpr) / dpr;
}
function getDPR(element) {
  const win = getWindow(element);
  return win.devicePixelRatio || 1;
}
function getSideAndAlignFromPlacement(placement) {
  const [side, align = "center"] = placement.split("-");
  return [side, align];
}
var popoverAvailable = typeof HTMLElement !== "undefined" && Object.hasOwn(HTMLElement.prototype, "popover");
function unwrapElement(element) {
  return !isElement(element) ? element.contextElement : element;
}

// src/overlay-positioner.setup.ts
function useOverlayPositioner(element, { state }) {
  const reference = referenceContext.consume(element);
  useOverlayPositionerState(element, state, { reference });
}
function useOverlayPositionerState(element, state, context) {
  useAnimationFrame(element, () => {
    const stateValues = mapValues(state);
    const referenceValue = context.reference.get();
    return () => updatePlacement(element, referenceValue, stateValues);
  });
}

// src/overlay-positioner.types.ts
var overlayPositionerProps = {
  strategy: { default: "absolute" },
  placement: { default: "top" },
  autoUpdate: { default: true },
  hoist: { default: true },
  transform: { default: false },
  offset: {
    default: 6,
    fromAttribute: (value) => {
      if (value == null) {
        return void 0;
      }
      try {
        return JSON.parse(value);
      } catch (e) {
        return void 0;
      }
    },
    toAttribute: JSON.stringify
  },
  flip: { default: true },
  shift: { default: true },
  overlap: { default: false },
  fitViewport: { default: false },
  sameWidth: { default: false },
  sameHeight: { default: false },
  inline: { default: false },
  hide: { default: false },
  boundary: { default: "clippingAncestors" },
  rootBoundary: { default: "viewport" },
  overflowPadding: { default: 4 },
  elementContext: { default: "floating" },
  altBoundary: { default: false }
};
var overlayPositionerEvents = {};

// src/overlay-root.setup.ts
import { createSignal } from "@aria-ui/core";
function useOverlayRoot(element) {
  const reference = createSignal(null);
  referenceContext.provide(element, reference);
}

// src/overlay-root.types.ts
var overlayRootProps = {};
var overlayRootEvents = {};

// src/elements.ts
var OverlayAnchorElement = class extends defineCustomElement({
  props: overlayAnchorProps,
  events: overlayAnchorEvents,
  setup: useOverlayAnchor
}) {
};
var OverlayPositionerElement = class extends defineCustomElement({
  props: overlayPositionerProps,
  events: overlayPositionerEvents,
  setup: useOverlayPositioner
}) {
};
var OverlayRootElement = class extends defineCustomElement({
  props: overlayRootProps,
  events: overlayRootEvents,
  setup: useOverlayRoot
}) {
};

export {
  useOverlayAnchor,
  overlayAnchorProps,
  overlayAnchorEvents,
  useOverlayPositioner,
  useOverlayPositionerState,
  overlayPositionerProps,
  overlayPositionerEvents,
  useOverlayRoot,
  overlayRootProps,
  overlayRootEvents,
  OverlayAnchorElement,
  OverlayPositionerElement,
  OverlayRootElement
};
